import { z } from '@hono/zod-openapi';

/**
 * Cursor-Paginierung ([V] 4.3 `Page<T>`).
 *
 * Hinweis: Cursor sind nicht signiert. Sicherheit entsteht durch `WHERE user_id = ctx.userId` in jeder
 * Abfrage (4.3-Regel); ein manipulierter Cursor kann nur eigene Daten verschieben.
 */

export const DEFAULT_PAGE_LIMIT = 25;
export const MAX_PAGE_LIMIT = 100;
export const CURSOR_MAX_LENGTH = 512;

const CURSOR_CHARSET = /^[A-Za-z0-9_-]+$/;

/** Cursor als Query-/Antwortwert: 1–512 Zeichen im Base64url-Zeichensatz. */
export const CursorString = z.string().min(1).max(CURSOR_MAX_LENGTH).regex(CURSOR_CHARSET);

/**
 * Query-Felder der Listenrouten. `@hono/zod-openapi` liefert Query-Werte als Strings; `limit` wird daher
 * über ein Ziffernmuster geparst (kein `z.coerce.number()`, das `""` zu 0 machen würde).
 *
 * OpenAPI-Parität: Muster (1–100) und Standardwert (Zeichenkette `'25'`, passend zum Zeichenketten-Typ)
 * stehen im Dokument so, wie das Schema sie prüft. Der Standard steht vor der Umwandlung in eine Zahl,
 * sonst erschiene er als Zahl bei Typ `string`; die Ausgabe von `parse` bleibt eine Zahl.
 */
export const PageQuery = z.strictObject({
  limit: z
    .string()
    .regex(/^([1-9]\d?|100)$/, 'limit muss eine ganze Zahl von 1 bis 100 sein')
    .default(String(DEFAULT_PAGE_LIMIT))
    .transform(Number)
    .pipe(z.int().min(1).max(MAX_PAGE_LIMIT)),
  cursor: CursorString.optional(),
});

/** Listenantwort: `{ items, nextCursor }`, strikt. */
export const pageOf = <Item extends z.ZodType>(item: Item) =>
  z.strictObject({ items: z.array(item), nextCursor: CursorString.nullable() });

const CursorKey = z.union([z.string(), z.number().finite(), z.boolean(), z.null()]);
type CursorKeyValue = z.infer<typeof CursorKey>;

/** Grundform des Cursor-Inhalts: Version 1, Sortierschlüssel `k`, eindeutige `id` als Tie-Breaker. */
export const CursorPayloadSchema = z.strictObject({
  v: z.literal(1),
  k: z.array(CursorKey),
  id: z.string().min(1),
});
export type CursorPayload = z.infer<typeof CursorPayloadSchema>;

export type CursorDecoded<T> = { ok: true; value: T } | { ok: false };

const toBase64Url = (bytes: Uint8Array): string => {
  let binary = '';
  for (const b of bytes) {
    binary += String.fromCharCode(b);
  }
  return btoa(binary).replaceAll('+', '-').replaceAll('/', '_').replaceAll('=', '');
};

const fromBase64Url = (s: string): Uint8Array => {
  const binary = atob(s.replaceAll('-', '+').replaceAll('_', '/'));
  return Uint8Array.from(binary, (c) => c.charCodeAt(0));
};

/** Kodiert den Cursor-Inhalt als Base64url(JSON); wirft `RangeError` bei mehr als 512 Zeichen. */
export function encodeCursor(
  payload: Readonly<{ v: 1; k: readonly CursorKeyValue[]; id: string }>,
): string {
  const encoded = toBase64Url(new TextEncoder().encode(JSON.stringify(payload)));
  if (encoded.length > CURSOR_MAX_LENGTH) {
    throw new RangeError(`Cursor länger als ${CURSOR_MAX_LENGTH} Zeichen`);
  }
  return encoded;
}

/**
 * Dekodiert und prüft einen Cursor (erst gegen die Grundform inkl. Version, dann gegen `schema`).
 * Wirft nie: manipulierte, zu lange oder falsch versionierte Cursor ergeben `{ ok: false }`.
 */
export function decodeCursor<S extends z.ZodType>(
  cursor: string,
  schema: S,
): CursorDecoded<z.output<S>> {
  if (!CursorString.safeParse(cursor).success) {
    return { ok: false };
  }
  let json: unknown;
  try {
    const text = new TextDecoder('utf-8', { fatal: true }).decode(fromBase64Url(cursor));
    json = JSON.parse(text);
  } catch {
    return { ok: false };
  }
  if (!CursorPayloadSchema.safeParse(json).success) {
    return { ok: false };
  }
  const parsed = schema.safeParse(json);
  return parsed.success ? { ok: true, value: parsed.data } : { ok: false };
}
